import repositoryData from "./repository.generated.json";

export type RepositoryCategoryCode =
  | "source"
  | "treatment"
  | "storage"
  | "conveyance"
  | "pumping"
  | "access"
  | "wastewater"
  | "agricultural"
  | "industrial"
  | "other";

export type QuickFactControl =
  | "text"
  | "number"
  | "integer"
  | "number_unit"
  | "select"
  | "multi_select";

export interface QuickFactDefinition {
  field: string;
  title: string;
  placeholder: string;
  help: string;
  control: QuickFactControl;
  control_label?: string;
  unit: string | null;
  min?: number;
  max?: number;
  options?: string[];
  required_if_known: boolean;
  dont_know_allowed: boolean;
}

export interface InfrastructureTypeDefinition {
  number: number;
  type_code: string;
  version: string;
  label: string;
  short_label: string;
  aliases: string[];
  category: RepositoryCategoryCode;
  category_colour: string;
  marker_shape: string;
  allowed_geometry: Array<"Point" | "LineString" | "Polygon">;
  assets: {
    marker_svg: string;
    marker_png: Record<string, string>;
    empty_state_svg: string;
    empty_state_png: string;
    aria_label: string;
    description: string;
    illustration_description: string;
  };
  quick_facts: QuickFactDefinition[];
  enrichment_cards: string[];
  sensitivity_default: "project-restricted" | "sensitive" | "public";
  duplicate_threshold_m: number;
}

export interface RepositoryCategory {
  code: Exclude<RepositoryCategoryCode, "other">;
  label: string;
  colour: string;
  shape: string;
  chip_svg: string;
}

export interface InfrastructureRepository {
  repository_version: string;
  style_reference: string;
  standard_empty_state_caption: string;
  categories: RepositoryCategory[];
  types: InfrastructureTypeDefinition[];
}

export const infrastructureRepository =
  repositoryData as InfrastructureRepository;

export const infrastructureTypes = infrastructureRepository.types;
export const repositoryCategories = infrastructureRepository.categories;

export function infrastructureType(typeCode: string) {
  return infrastructureTypes.find((item) => item.type_code === typeCode);
}

export function assetUrl(path: string) {
  return `${import.meta.env.BASE_URL}${path.replace(/^\/+/, "")}`;
}

export function matchesInfrastructureType(
  definition: InfrastructureTypeDefinition,
  query: string,
) {
  const normalised = query.trim().toLowerCase();
  if (!normalised) return true;
  return [
    definition.type_code,
    definition.label,
    definition.short_label,
    ...definition.aliases,
  ].some((value) => value.toLowerCase().includes(normalised));
}

export function defaultGeometryType(typeCode: string) {
  const definition = infrastructureType(typeCode);
  return definition?.allowed_geometry.includes("Point")
    ? "Point"
    : (definition?.allowed_geometry[0] ?? "Point");
}
